import PrebidBootstrap from './prebid/PrebidBootstrap'
import AuctionTracker from './prebid/AuctionTracker'
import { toPrebidUnits } from './adapters'
import { getAdSlots } from './AdSlotUtility'
import { getContainer } from './container/containers'
import {
  aliasBidders,
  bidderForCode,
  ecpmOrDefault,
  filterSizes,
  flatMapBids,
  injectAltSizes,
  playerDims,
  renderAd,
  unalias
} from './utils'
import { getButtons } from './container/buttons'
import HookRunner from './hooks/HookRunner'
import LazyLoad from './LazyLoad'
import FillManager from './FillManager'
import ThirdPartyAuctionTracker from './ThirdPartyAuctionTracker'
import { ajax } from './ajax'
import NativoStartHook from './hooks/NativoStartHook'
import AppnexusStartHook from './hooks/AppnexusStartHook'
import OutbrainStartHook from './hooks/OutbrainStartHook'
import VidazooStartHook from './hooks/VidazooStartHook'
import PassbackStartHook from './hooks/PassbackStartHook'
import GdprStartHook from './hooks/GdprStartHook'
import VastStartHook from './hooks/VastStartHook'
import AdPlusStartHook from './hooks/AdPlusStartHook'
import VmgMap from './hooks/VmgMap'
import Logger from './Log'
import BidderStyleHook from './hooks/BidderStyleHook'
import BidderPriorityStartHook from './hooks/BidderPriorityStartHook'
import TagCssStartHook from './hooks/TagCssStartHook'
import InstreamHook from './hooks/InstreamHook'
import JsTagHook from './hooks/JsTagHook'
import { CarbonTracker } from './CarbonTracker'
import AvantisHook from './hooks/AvantisHook'
import DistroScaleStartHook from './hooks/DistroScaleStartHook'
import ConfigEnhanceHook from './hooks/ConfigEnhanceHook'
import NobidHook from './hooks/NobidHook'
import UdmStartHook from './hooks/UdmStartHook'

const Log = Logger.for('Main')

export class Main {
  constructor (config, currentScript) {
    Logger.name('SMRTB ' + config.zoneId())

    Log.debug('Init SmartTag v' + 1.2)

    this._config = config
    this._hooks = new HookRunner()
    this._hooks.register(new ConfigEnhanceHook())
    this._hooks.register(new GdprStartHook())
    this._hooks.register(new BidderPriorityStartHook())
    this._hooks.register(new VidazooStartHook())
    this._hooks.register(new NativoStartHook())
    this._hooks.register(new DistroScaleStartHook())
    this._hooks.register(new AvantisHook())
    this._hooks.register(new PassbackStartHook())
    this._hooks.register(new AppnexusStartHook())
    this._hooks.register(new OutbrainStartHook())
    this._hooks.register(new UdmStartHook())
    this._hooks.register(new InstreamHook())
    this._hooks.register(new VastStartHook())
    this._hooks.register(new AdPlusStartHook())
    this._hooks.register(new BidderStyleHook())
    this._hooks.register(new TagCssStartHook())
    this._hooks.register(new NobidHook())
    this._hooks.register(new JsTagHook())

    this._slots = getAdSlots(config.container(), config.zoneId(), currentScript)
    if (this._slots.length === 0) {
      throw Error('Couldnt find any target ad containers, quitting')
    }

    this._container = getContainer(config.container().type())
    if (!this._container) {
      throw Error('Unknown or unconfigured container type ' + config.container().type())
    }
  }

  /**
   * Attempt to fill specific slot across all fill methods.
   * This method will return once all auctions or fill attempts
   * have been made. Some third party fill methods may have known
   * clears as soon as this returns, some such as hb bids may clear
   * at a later time
   * @param slot target slot
   * @param bids list of eligible bids, possibly shared with other slots
   * @param otherTracker thirdpartyauctiontracker if applicable
   * @param carbonTracker CarbonTracker to report auction data to carbon for temp reporting
   * @returns {Promise<bid|void>} winning bid object if any, null if no fill
   * @private
   */
  async _tpFill (slot, bids, tracker, otherTracker, carbonTracker) {
    const container = this._container
    const config = this._config

    await new Promise((resolve, reject) => {
      setTimeout(() => {
        slot.attach(container.parentIsTarget())
        resolve()
      })
    })

    const topCpm = bids.length !== 0 ? bids[0].cpm : 0

    const methods = FillManager.methods().filter(m => m.estValue() > topCpm)
    Log.debug(methods.length + ' fill methods with value above hb bid of ' + topCpm)
    for (const method of methods) {
      otherTracker.bidRequest(method.name(), config.media())
      carbonTracker.request(method.name())

      let bid = null
      try {
        bid = await method.fill(slot, config.media())
      } catch (e) {
        Log.error('Exception during fill method ' + method.name() + ': ' + e)
        console.error(e)
        continue
      }

      Log.debug(method.name() + ' complete')
      otherTracker.addBidResponse(method.name(), method.format(), bid.cpm, [bid.width, bid.height])
      bid.mediaType = method.format()
      bid.bidder = method.name()
      bid.auctionId = otherTracker.auctionId()

      if (bid.cpm > 0) {
        carbonTracker.bid({ bidder: method.name(), price: bid.cpm, format: method.format() })
        if (bid.ad) {
          // returning normal bid to compete against other prebid bids and render like normal
          bids.push(bid)
          bids = bids.sort((a, b) => b.cpm - a.cpm) // resort into proper place
          continue
        }

        container.style(bid, slot, config.media())
        await this._hooks.run('STYLE', bid, slot)
        otherTracker.bidWon(method.name(), true)
        Log.info('Fill method ' + method.name() + ' cleared slot at price ' + bid.cpm)
        return bid
      }

      Log.debug('Fill method ' + method.name() + ' did not fill, continuing')
    }

    if (bids.length === 0) {
      const passback = VmgMap.passback()
      if (passback) {
        Log.debug('Found passback, rendering')
        bids.push(passback)
      } else {
        Log.info('All fill methods failed including auctions and passback, no fill')
        return null
      }
    }

    Log.info('No third party fill methods available or cleared, but have hb bid or pb available')

    const bid = bids.shift()
    Log.debug('Rendering bid ' + bid.requestId + ' from ' + bid.bidder + ' price ' + bid.cpm)
    const dims = playerDims(config.media(), bid)

    container.style(bid, slot, config.media())
    await this._hooks.run('STYLE', bid, slot)

    const nurl = bid.cpm !== 0 ? tracker.bidWon(bid) : null

    const cb = async () => {
      await renderAd(bid, slot, config.media())

      Log.info('renderAd impression cleared for slot ' + slot.nodeId())
      if (nurl) {
        Log.debug('Firing imp beacon ' + nurl)
        setTimeout(() => {
          ajax(nurl).then(() => {
            Log.debug('Successful beacon fire')
          }).catch(e => {
            Log.warn('Failed to fire impression beacon: ' + e)
          })
        }, 150)
      }
    }

    if (config.container().lazyLoad() === true) {
      Log.debug('Lazy loading enabled, will render when in view')
      return new Promise((resolve, reject) => {
        const lazy = new LazyLoad(slot.node(), dims[1], () => {
          Log.info('Target in view, rendering creative')
          cb().then(() => lazy.stop()).then(() => resolve())
        })
      }).then(() => bid)
    } else {
      Log.debug('Lazy load disabled, rendering immediately')
      return cb().then(() => bid)
    }
  }

  async _auction (config, slots, pb, adUnits, tracker) {
    const carbonTracker = new CarbonTracker(config.zoneId())

    let bids = []
    let auctionId = null
    if (config.bidders() && config.bidders().length !== 0) {
      Log.debug('Have ' + config.bidders().length + ' hb partners')

      // register aliases for multiple entries of the same bidder
      // codes of _X naming is present, implying multiple entries
      config.bidders().filter(b => b.bidder.indexOf('_') > -1).forEach(b => {
        const ub = unalias(b.bidder)
        pb.aliasBidder(ub, b.bidder)
      })

      config.bidders().forEach(b => {
        carbonTracker.request(unalias(b.bidder))
      })

      const res = await new Promise((resolve, reject) => {
        pb.requestBids(adUnits, async (units, auctionId) => {
          resolve({ units, auctionId })
        }, 2)
      })
      bids = flatMapBids(res.units)
      auctionId = res.auctionId

      Log.debug('Sorted bids: ' + JSON.stringify(bids))
    } else {
      auctionId = tracker.initEmptyAuction()
    }

    Log.debug('Received bids: ' + bids.length)

    for (const bid of bids) {
      // May come from a config option, or a query param for dynamic pb
      // bidder testing by increasing their price priority if a bid present
      const bidder = bidderForCode(bid.bidder, config.bidders())
      const ecpm = ecpmOrDefault(bidder.params.ecpm, 0)

      if (ecpm > bid.cpm) {
        Log.info('Found override ecpm value for ' + bidder +
          ' ' + bid.cpm + ' -> ' + ecpm)
        bid.cpm = ecpm
      }

      carbonTracker.bid({ bidder: unalias(bidder.bidder), price: bid.cpm, format: bid.mediaType })
    }

    let filled = false // if at least one slot filled (typically only have 1 avail)
    for (const slot of slots) {
      let bid = null
      for (const b of bids) {
        if (b.adUnitCode === slot.nodeId()) {
          // we use this to find the correct transactionId even
          // if we share ads across similar units
          bid = b
          break
        }
      }

      const transactionId = bid ? bid.transactionId : tracker.genTransactionId()

      const win = await this._tpFill(slot, bids, tracker,
        new ThirdPartyAuctionTracker(tracker, auctionId, transactionId), carbonTracker)

      if (!win) { // null means no fill at all, win.cpm=0 means passback
        Log.debug('No fill for slot, skipping button decorators')
        continue
      }

      filled = true

      carbonTracker.bidWon({ bidder: unalias(win.bidder), price: win.cpm, format: win.mediaType })

      if (config.container().closeable()) {
        getButtons(slot)
      }

      config.pixels().forEach(pixel => {
        Log.debug('Firing third party config pixel ' + pixel)
        ajax(pixel)
      })
    }

    Log.info('All slot auctions complete')

    tracker._onAuctionEnd(auctionId)
    carbonTracker.submit()

    return filled
  }

  async run () {
    const config = this._config
    const slots = this._slots
    const container = this._container
    const tracker = new AuctionTracker(config.zoneId())

    if (!container.supported()) {
      return Log.warn('Container ' + container.type() + ' not supported, quitting')
    }

    // important this be after hooks in case they modify config
    const pb = new PrebidBootstrap(tracker)

    await this._hooks.run('START', config, pb)
    Log.debug('All hooks completed')

    aliasBidders(config) // only affects pbjs bidders with multiple biddercode entries

    pb.setConfig(config.prebidOpts())

    Log.debug('Set prebid config, generating ad units')

    const allAddUnits = toPrebidUnits(config.media(), config.bidders(), slots)
    const maxDims = container.maxSlotDims(slots)
    Log.debug('Max slot dimensions detected ' + maxDims[0] + 'x' + maxDims[1])
    const adUnits = filterSizes(allAddUnits, maxDims)
    if (config.altSizes() && config.media().banner) {
      Log.debug('Injecting alt sizes')
      injectAltSizes(config.media().banner)
    }

    if (adUnits.length === 0) {
      return Log.info('No units passed size filtering, quitting')
    }

    let filled = await this._auction(config, slots, pb, adUnits, tracker)
    for (let x = 0; x < 10; x++) {
      if (filled && config.refresh() < 10) break

      await new Promise((resolve, reject) => {
        setTimeout(resolve, (config.refresh() * 1000) + 500)
      })

      slots.forEach(s => s.resetNode())
      filled = await this._auction(config, slots, pb, adUnits, tracker)

      Log.info('Refreshed or reauctioned ad unit')
    }
  }
}
